import { X, Check } from 'lucide-react';
import { createPortal } from 'react-dom';
import { SUPPORTED_LANGUAGES } from '../../lib/languages';
import { SupportedLanguage } from '../../lib/expenseTypeHelpers';

interface LanguagePickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (lang: SupportedLanguage) => void;
    currentLanguage: string;
    title: string;
}

export function LanguagePickerModal({
    isOpen,
    onClose,
    onSelect,
    currentLanguage,
    title
}: LanguagePickerModalProps) {
    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Card */}
            <div className="relative bg-white dark:bg-[#00172D] rounded-[2.5rem] shadow-2xl w-full max-w-sm overflow-hidden flex flex-col max-h-[80vh] transform transition-all scale-100 animate-in fade-in zoom-in-95 duration-200 border border-transparent dark:border-white/5">
                <div className="p-8 pb-4 flex-shrink-0 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white dark:hover:bg-white/5 rounded-full transition-colors"
                    >
                        <X size={20} />
                    </button>

                    <div className="text-center mb-2 mt-2">
                        <h3 className="text-2xl font-black text-gray-900 dark:text-white">{title}</h3>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 pb-8 no-scrollbar">
                    <div className="space-y-2">
                        {SUPPORTED_LANGUAGES.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => {
                                    console.log('>>> [LanguagePickerModal] Clicked:', lang.code);
                                    onSelect(lang.code);
                                }}
                                className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all active:scale-[0.98] ${currentLanguage === lang.code
                                    ? 'bg-blue-50 dark:bg-blue-500/10 border-2 border-blue-500/20'
                                    : 'hover:bg-gray-50 dark:hover:bg-white/5 border-2 border-transparent'
                                    }`}
                            >
                                <span className="text-2xl flex-shrink-0">{lang.flag}</span>
                                <div className="flex-1 text-left min-w-0">
                                    <h4 className={`font-bold truncate ${currentLanguage === lang.code ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'
                                        }`}>
                                        {lang.nativeName}
                                    </h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                        {lang.name}
                                    </p>
                                </div>
                                {currentLanguage === lang.code && (
                                    <div className="w-6 h-6 rounded-full bg-blue-600 dark:bg-blue-400 flex items-center justify-center">
                                        <Check size={14} className="text-white" />
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
